import { ERROR_CODES, ErrorCode } from '@/lib/constants/error-codes'
import { AxiosError } from 'axios'
import { useCurrentLocale, useScopedI18n } from '@/locales/client'
import { toast } from 'sonner'
import { getDaysAndHoursAndMinutes } from '@/lib/utils/date-ranges'

interface APIError {
  traceId: string
  errorCode: ErrorCode
  errors: string[]
}

export const useApiErrors = () => {
  const t = useScopedI18n('common.error') as (
    key: string,
    args?: Record<string, unknown>
  ) => string
  const tWords = useScopedI18n('common.words')
  const locale = useCurrentLocale()

  /**
   * Extracts API error information from Axios error
   */
  const extractApiError = (error: unknown): APIError | null => {
    if (!(error instanceof AxiosError) || !error.response?.data) {
      return null
    }

    const { data } = error.response

    if (
      data.errorCode &&
      typeof data.errorCode === 'string' &&
      Object.values(ERROR_CODES).includes(data.errorCode as ErrorCode)
    ) {
      return {
        errorCode: data.errorCode,
        errors: Array.isArray(data.errors) ? data.errors : [],
        traceId: data.traceId ?? '',
      }
    }

    return null
  }

  /**
   * Gets the error message for display
   */
  const getErrorMessage = (error: unknown, defaultMessage: string): string => {
    const apiError = extractApiError(error)

    if (apiError) {
      const { errorCode, errors } = apiError
      if (errorCode === ERROR_CODES.USER_BLOCKED) {
        const { hours, minutes } = getDaysAndHoursAndMinutes(
          Number(errors[0]),
          'minutes',
          'hours'
        )
        return t(errorCode, {
          duration: `${hours > 0 ? tWords('iban_blocked_hours', { count: hours }) : ''}${minutes > 0 ? tWords('iban_blocked_minutes', { count: minutes }) : ''}`,
        })
      }
      return t(errorCode, Object.fromEntries(errors.entries()))
    }

    return error instanceof Error ? error.message : defaultMessage
  }

  /**
   * Shows a toast error message based on an API error
   */
  const showErrorToast = ({
    error,
    defaultMessage = t('something_went_wrong'),
    excludedErrorCodesFromToast = [],
  }: {
    error: unknown
    defaultMessage?: string
    excludedErrorCodesFromToast?: ErrorCode[]
  }): void => {
    if (
      error instanceof AxiosError &&
      excludedErrorCodesFromToast.includes(
        error.response?.data.errorCode as ErrorCode
      )
    ) {
      return
    }

    const errorMessage = getErrorMessage(error, defaultMessage)
    toast.error(errorMessage, {
      position: locale === 'ar' ? 'bottom-right' : 'bottom-left',
      className: locale === 'ar' ? 'font-arabic' : '',
    })
  }

  return {
    showErrorToast,
  }
}
